import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Table, 
  Card, 
  Button, 
  Input, 
  Select, 
  Space, 
  Tag, 
  Modal, 
  Form, 
  message,
  Dropdown,
  Upload,
  Alert,
  Row,
  Col,
  Typography,
  Spin,
  List,
  Popconfirm,
  Tabs
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined,
  MoreOutlined,
  EyeOutlined,
  UserAddOutlined,
  UploadOutlined,
  HistoryOutlined,
  ShareAltOutlined,
  SearchOutlined,
  HomeOutlined,
  BankOutlined,
  BuildOutlined,
  ApartmentOutlined,
  ShopOutlined,
  BankOutlined as BankIcon,
  WarningOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import { useCognitoAuth } from '../contexts/CognitoAuthContext';
import { useApp } from '../contexts/AppContext';
import { getUserIdAsNumber } from '../utils/cognitoUtils';
import { isAdmin } from '../utils/roleUtils';
import apiService, { Lead, User } from '../services/api';
import dayjs from 'dayjs';
import {
  TELECOM_STATUSES,
  ENERGY_STATUSES,
  TELECOM_SECTIONS,
  ENERGY_SECTIONS
} from './statusConstants';
import { read, utils } from 'xlsx';
import LeadDetailsModal from './LeadDetailsModal';
import NotesPanel from './NotesPanel';
import { getLeadCategoryStyle } from '../utils/leadCategoryColors';
import './Leads.css';

const { Option } = Select;
const { Title, Text } = Typography;

const Leads: React.FC = () => {
  const { user, isLoading } = useCognitoAuth();
  const { currentBusinessArea } = useApp();
  

  
  // CSS for duplicate lead highlighting and batch operations
  const duplicateLeadStyles = `
    .duplicate-lead-row {
      background-color: #fff2e8 !important;
      border-left: 3px solid #fa8c16 !important;
    }
    
    .batch-operating {
      pointer-events: none;
    }
    
    .batch-operating * {
      transition: none !important;
    }
    
  `;
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [importing, setImporting] = useState(false);
  const [formKey, setFormKey] = useState(0); // Force re-render when customer type changes
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [form] = Form.useForm();
  const [selectionMode, setSelectionMode] = useState<'none' | 'assign' | 'share' | 'delete'>('none');
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [batchResponsible, setBatchResponsible] = useState<number | undefined>(undefined);
  const [batchShareAreas, setBatchShareAreas] = useState<string[]>([]);
  const [batchShareUsers, setBatchShareUsers] = useState<number[]>([]);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [selectedImportType, setSelectedImportType] = useState<string | null>(null);
  const [importFiles, setImportFiles] = useState<{ [key: string]: File | null }>({
    intra: null,
    '4000Junho': null,
    leads: null
  });
  const [previewData, setPreviewData] = useState<{ [key: string]: any[] }>({
    intra: [],
    '4000Junho': [],
    leads: []
  });
  const [previewLoading, setPreviewLoading] = useState<{ [key: string]: boolean }>({
    intra: false,
    '4000Junho': false,
    leads: false
  });
  const [duplicateLeads, setDuplicateLeads] = useState<any[]>([]);
  const [duplicateStats, setDuplicateStats] = useState<any>(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [resolvingDuplicate, setResolvingDuplicate] = useState(false);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [combinedData, setCombinedData] = useState<any>(null);
  const [duplicateSelectionMode, setDuplicateSelectionMode] = useState(false);
  const [selectedDuplicateKeys, setSelectedDuplicateKeys] = useState<React.Key[]>([]);
  const [deletingDuplicates, setDeletingDuplicates] = useState(false);
  const [clearingHistory, setClearingHistory] = useState(false);
  const [leadCategory, setLeadCategory] = useState<number>(0);
  
  // Add state for pageSize and LeadDetailsModal
  const [pageSize, setPageSize] = useState(10);
  const [leadDetailsModalVisible, setLeadDetailsModalVisible] = useState(false);
  const [selectedLeadForDetails, setSelectedLeadForDetails] = useState<Lead | null>(null);
  
  
  // Search functionality
  const [searchText, setSearchText] = useState('');
  const [filteredLeads, setFilteredLeads] = useState<Lead[]>([]);

  const fetchLeads = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      // For team leaders and above, pass undefined to get all team leads
      // For regular users, pass user.id to get only their leads
      const targetUserId = (user.role === 'team_leader' || user.role === 'admin' || user.role === 'super_admin') 
        ? undefined 
        : user.id;
      
      const data = await apiService.getLeads(currentBusinessArea, getUserIdAsNumber(user));
      setLeads(data);
      setFilteredLeads(data); // Initialize filtered leads with all leads
      

    } catch (error) {
      message.error('Erro ao carregar leads.');
    }
    setLoading(false);
  }, [user, currentBusinessArea]);

  // Filter leads based on search text
  useEffect(() => {
    if (!searchText.trim()) {
      setFilteredLeads(leads);
    } else {
      const filtered = leads.filter(lead => {
        const searchLower = searchText.toLowerCase();
        return (
          lead.name?.toLowerCase().includes(searchLower) ||
          lead.email?.toLowerCase().includes(searchLower) ||
          lead.phone?.toLowerCase().includes(searchLower) ||
          lead.company?.toLowerCase().includes(searchLower) ||
          lead.dif?.toLowerCase().includes(searchLower) ||
          lead.address?.toLowerCase().includes(searchLower) ||
          lead.city?.toLowerCase().includes(searchLower)
        );
      });
      setFilteredLeads(filtered);
    }
  }, [searchText, leads]);

  // Clear selection when search text changes (but not when leads data changes)
  const prevSearchTextRef = useRef(searchText);
  useEffect(() => {
    if (prevSearchTextRef.current !== searchText && selectedRowKeys.length > 0) {
      setSelectedRowKeys([]);
    }
    prevSearchTextRef.current = searchText;
  }, [searchText, selectedRowKeys.length]);

  const fetchAvailableUsers = useCallback(async () => {
    if (!user) return;
    try {
      const assignableUsers = await apiService.getAssignableUsers();
      setAvailableUsers(assignableUsers);
    } catch (error) {
      message.error('Erro ao carregar utilizadores disponíveis.');
    }
  }, [user]);

  const fetchDuplicateLeads = useCallback(async () => {
    // Temporary: Remove admin check for testing
    // if (!user || !isAdmin(user.role || '')) return;
    try {
      const response = await apiService.getDuplicatesAndAudit();
      if (response.success) {
        setDuplicateLeads(response.duplicates);
        setAuditLogs(response.audit_logs);
        setDuplicateStats(response.stats);
        setCombinedData(response);
      }
    } catch (error) {
      // Handle error silently
    }
  }, []);

  const fetchDuplicateStats = useCallback(async () => {
    // Temporary: Remove admin check for testing
    // if (!user || !isAdmin(user.role || '')) return;
    try {
      const response = await apiService.getDuplicateStats();
      if (response.success) {
        setDuplicateStats(response.stats);
      }
    } catch (error) {
      // Handle error silently
    }
  }, []);

  const handleResolveDuplicate = async (duplicateId: number, action: 'edit' | 'delete' | 'merge', data?: any) => {
    setResolvingDuplicate(true);
    try {
      const response = await apiService.resolveDuplicateLead(duplicateId, action, data);
      if (response.success) {
        message.success(response.message);
        fetchDuplicateLeads();
        fetchDuplicateStats();
        fetchLeads(); // Refresh main leads list
      } else {
        message.error(response.message || 'Erro desconhecido ao resolver lead duplicado.');
      }
    } catch (error) {
      message.error('Erro ao resolver lead duplicado. Verifique a consola para mais detalhes.');
    }
    setResolvingDuplicate(false);
  };

  useEffect(() => {
    fetchLeads();
    fetchAvailableUsers();
    // Temporary: Fetch for all users during testing
    fetchDuplicateLeads();
    fetchDuplicateStats();
  }, [fetchLeads, fetchAvailableUsers, fetchDuplicateLeads, fetchDuplicateStats]);

  const showModal = (lead?: Lead) => {
    setEditingLead(lead || null);
    if (lead) {
      let postal_code_4 = '';
      let postal_code_3 = '';
      if (lead.postal_code && lead.postal_code.includes('-')) {
        [postal_code_4, postal_code_3] = lead.postal_code.split('-');
      }
      form.setFieldsValue({
        ...lead,
        business_area: lead.business_area === 'telecommunications' ? 'telecommunications' : 'energy_solutions',
        postal_code_4,
        postal_code_3,
        lead_status: 'Entrada', // Always Entrada
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        business_area: currentBusinessArea,
        customer_type: 'residential',
        lead_status: 'Entrada',
      });
    }
    setIsModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const postal_code = `${values.postal_code_4}-${values.postal_code_3}`;
      const leadData = {
        ...values,
        postal_code,
        lead_status: 'Entrada', // Always Entrada
        created_by: user?.id,
        business_area: [values.business_area || currentBusinessArea], // Convert to array format
        customer_type: values.customer_type || 'residential',
      };
      delete leadData.postal_code_4;
      delete leadData.postal_code_3;
      
      if (editingLead) {
        await apiService.updateLead(editingLead.id, leadData);
        message.success('Lead atualizado com sucesso!');
      } else {
        await apiService.createLead(leadData);
        message.success('Lead criada com sucesso!');
      }
      setIsModalVisible(false);
      form.resetFields();
      fetchLeads();
    } catch (error) {
      message.error('Erro ao salvar lead. Verifique os dados inseridos.');
    }
  };

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: 'Confirmar exclusão',
      content: 'Tem certeza que deseja excluir este lead?',
      onOk: async () => {
        try {
          await apiService.deleteLead(id);
          message.success('Lead excluído com sucesso!');
          fetchLeads();
        } catch (error) {
          message.error('Erro ao excluir lead.');
        }
      },
    });
  };

  const handleImport = async (file: File) => {
    setImporting(true);
    try {
      if (currentBusinessArea === 'telecommunications') {
        await apiService.importTelecomLeads(file);
      } else {
        await apiService.importEnergyLeads(file);
      }
      message.success('Leads importados com sucesso!');
      setIsImportModalVisible(false);
      fetchLeads();
    } catch (error) {
      message.error('Erro ao importar leads.');
    }
    setImporting(false);
  };

  const handleFileUpload = async (importType: string, file: File) => {
    setImportFiles(prev => ({ ...prev, [importType]: file }));
    setSelectedImportType(importType);
    
    // Generate preview for the file
    setPreviewLoading(prev => ({ ...prev, [importType]: true }));
    try {
      const preview = await generatePreview(file);
      setPreviewData(prev => ({ ...prev, [importType]: preview }));
    } catch (error) {
      message.error('Erro ao gerar preview do arquivo.');
    }
    setPreviewLoading(prev => ({ ...prev, [importType]: false }));
  };

  const generatePreview = async (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = utils.sheet_to_json(worksheet, { header: 1 });
          
          // Return first 5 rows for preview
          const preview = jsonData.slice(0, 5);
          resolve(preview);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  const handleImportSubmit = async () => {
    if (!selectedImportType || !importFiles[selectedImportType]) {
      message.error('Por favor selecione um arquivo para importar.');
      return;
    }

    setImporting(true);
    try {
      const file = importFiles[selectedImportType]!;
      
      if (selectedImportType === '4000Junho') {
        // Use existing import logic - 4000Junho is always telecom format
        const response = await apiService.importTelecomLeads(file, currentBusinessArea, leadCategory);
        
        if (response.success) {
          message.success(response.message);
          
          // Show duplicate warning if any
          if (response.duplicate_count > 0) {
            message.warning(
              `${response.duplicate_count} leads foram identificados como duplicados e estão pendentes de resolução por um administrador.`
            );
          }
        } else {
          message.error(response.message || 'Erro ao importar leads.');
        }
      } else if (selectedImportType === 'leads') {
        // Use new leads format import logic
        const response = await apiService.importLeadsFormat(file, currentBusinessArea, leadCategory);
        
        if (response.success) {
          message.success(response.message);
          
          // Show duplicate warning if any
          if (response.duplicate_count > 0) {
            message.warning(
              `${response.duplicate_count} leads foram identificados como duplicados e estão pendentes de resolução por um administrador.`
            );
          }
        } else {
          message.error(response.message || 'Erro ao importar leads.');
        }
      } else if (selectedImportType === 'intra') {
        // Use new intra format import logic
        const response = await apiService.importIntraFormat(file, currentBusinessArea, leadCategory);
        
        if (response.success) {
          message.success(response.message);
          
          // Show duplicate warning if any
          if (response.duplicate_count > 0) {
            message.warning(
              `${response.duplicate_count} leads foram identificados como duplicados e estão pendentes de resolução por um administrador.`
            );
          }
        } else {
          message.error(response.message || 'Erro ao importar leads.');
        }
      } else {
        
        message.info(`Importação para ${selectedImportType} será implementada em breve.`);
      }
      
      setImportModalVisible(false);
      setSelectedImportType(null);
      setImportFiles({ intra: null, '4000Junho': null, leads: null });
      setPreviewData({ intra: [], '4000Junho': [], leads: [] });
      setLeadCategory(0);
      fetchLeads();
      
      // Refresh duplicate data immediately after import
      fetchDuplicateLeads();
      fetchDuplicateStats();
    } catch (error) {
      message.error('Erro ao importar leads.');
    }
    setImporting(false);
  };

  const getStatusColor = (status: string) => {
    const statusColors: { [key: string]: string } = {
      // Entrada section
      'Novo': 'blue',
      'Contactado': 'cyan',
      'Qualificado': 'geekblue',
      'Entrada': 'default',
      
      // Feedback section
      'Interessado': 'orange',
      'Não Interessado': 'red',
      'Sem Interesse': 'red',
      'Pendente': 'gold',
      'Follow up': 'yellow',
      'Follow-up': 'yellow',
      
      // Proposta Comercial section
      'Proposta Enviada': 'purple',
      'Proposta Aceite': 'green',
      'Proposta Rejeitada': 'red',
      
      // Documentos da Venda section
      'Documentos Pendentes': 'orange',
      'Documentos Completos': 'green',
      
      // Validação section
      'Em Validação': 'processing',
      'Validado': 'green',
      'Rejeitado': 'red',
      
      // Agendamento e Instalação section
      'Agendado': 'blue',
      'Instalado': 'green',
      'Cancelado': 'red',
      
      // Final states
      'Fechado': 'green',
      'Fecho': 'green',
      'Fidelizado': 'orange',
      'Perdido': 'red',
      
      // Error/Invalid states
      'Informação Errada': 'purple',
      'Informação': 'purple',
      'Dados Incorretos': 'red',
      
      // Assignment states
      'Não Atribuido': 'black',
      'Não Atendeu': 'blue',
      
      // Default
      'Por definir': 'default'
    };
    return statusColors[status] || 'default';
  };

  const getAreaColor = (area: string) => {
    return area === 'telecommunications' ? 'blue' : 'green';
  };

  const handleBatchAssign = async () => {
    if (!batchResponsible || selectedRowKeys.length === 0) {
      message.error('Por favor selecione um responsável e pelo menos um lead.');
      return;
    }

    try {
      await apiService.batchAssignLeads(selectedRowKeys, batchResponsible, getUserIdAsNumber(user));
      message.success(`${selectedRowKeys.length} leads atribuídos com sucesso!`);
      setSelectedRowKeys([]);
      setBatchResponsible(undefined);
      setShowAssignmentModal(false);
      setSelectionMode('none');
      fetchLeads();
    } catch (error) {
      message.error('Erro ao atribuir leads.');
    }
  };

  const handleBatchShare = async () => {
    if (batchShareAreas.length === 0 || batchShareUsers.length === 0 || selectedRowKeys.length === 0) {
      message.error('Por favor selecione pelo menos uma área, um utilizador e pelo menos um lead.');
      return;
    }
    try {
      // For each selected lead, update business_area and assignments (API call needed)
      await Promise.all(selectedRowKeys.map(async (leadId) => {
        // Fetch the lead to get current business_area
        const lead = leads.find(l => l.id === leadId);
        if (!lead) return;
        let newAreas = Array.isArray(lead.business_area) ? [...lead.business_area] : [lead.business_area];
        batchShareAreas.forEach(area => {
          if (!newAreas.includes(area)) newAreas.push(area);
        });
        await apiService.updateLead(leadId, { business_area: JSON.stringify(newAreas) });
        // Optionally, assign users (multi-user assignment logic can be added here)
        // For now, just update business_area
      }));
      message.success('Leads partilhados com sucesso!');
      setSelectedRowKeys([]);
      setBatchShareAreas([]);
      setBatchShareUsers([]);
      fetchLeads();
    } catch (error) {
      message.error('Erro ao partilhar leads.');
    }
  };

  const handleBatchDelete = async () => {
    if (!selectedRowKeys.length) {
      message.error('Por favor selecione pelo menos um lead.');
      return;
    }

    try {
      setLoading(true);
      let successCount = 0;
      let errorCount = 0;

      // Use Promise.allSettled to handle all deletions concurrently
      const results = await Promise.allSettled(
        selectedRowKeys.map(async (leadId) => {
          const result = await apiService.deleteLead(leadId);
          return { leadId, result };
        })
      );

      // Count successes and failures
      results.forEach(result => {
        if (result.status === 'fulfilled') {
          const { result: deleteResult } = result.value;
          if (deleteResult.success) {
            successCount++;
          } else {
            errorCount++;
          }
        } else {
          errorCount++;
        }
      });
      
      if (successCount > 0) {
        message.success(`${successCount} leads eliminados com sucesso!`);
      }
      if (errorCount > 0) {
        message.error(`${errorCount} leads não puderam ser eliminados. Verifique as permissões ou se os leads têm dados relacionados.`);
      }

      setSelectionMode('none');
      setSelectedRowKeys([]);
      
      // Add a small delay before refreshing to avoid rapid re-renders
      setTimeout(() => {
        fetchLeads();
      }, 100);
    } catch (error) {
      message.error('Erro ao eliminar leads.');
    } finally {
      setLoading(false);
    }
  };

  const handleBatchDeleteDuplicates = async () => {
    if (!selectedDuplicateKeys.length) {
      message.error('Por favor selecione pelo menos um lead duplicado.');
      return;
    }

    try {
      setDeletingDuplicates(true);
      
      // Store the keys to delete before clearing selection
      const keysToDelete = [...selectedDuplicateKeys];
      
      // Clear selection immediately to prevent UI updates
      setSelectedDuplicateKeys([]);
      setDuplicateSelectionMode(false);
      
      // Wait for UI to settle
      await new Promise(resolve => setTimeout(resolve, 100));
      
      let successCount = 0;
      let errorCount = 0;

      // Process deletions sequentially to avoid overwhelming the UI
      for (const duplicateId of keysToDelete) {
        try {
          const response = await apiService.resolveDuplicateLead(Number(duplicateId), 'delete');
          if (response.success) {
            successCount++;
          } else {
            errorCount++;
          }
        } catch (error) {
          errorCount++;
        }
        
        // Small delay between each deletion
        await new Promise(resolve => setTimeout(resolve, 50));
      }

      // Show results
      if (successCount > 0) {
        message.success(`${successCount} leads duplicados eliminados com sucesso!`);
      }
      if (errorCount > 0) {
        message.error(`${errorCount} leads duplicados não puderam ser eliminados.`);
      }
      
      // Wait before refreshing data
      await new Promise(resolve => setTimeout(resolve, 200));
      fetchDuplicateLeads();
      fetchDuplicateStats();
      fetchLeads(); // Refresh main leads table
      
    } catch (error) {
      message.error('Erro ao eliminar leads duplicados.');
    } finally {
      setDeletingDuplicates(false);
    }
  };

  const handleClearImportHistory = async () => {
    try {
      setClearingHistory(true);
      const response = await apiService.clearImportHistory();
      if (response.success) {
        message.success(response.message);
        // Refresh audit logs
        fetchDuplicateLeads();
      } else {
        message.error(response.message || 'Erro ao limpar histórico de importações.');
      }
    } catch (error) {
      message.error('Erro ao limpar histórico de importações.');
    } finally {
      setClearingHistory(false);
    }
  };

  const columns = [
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: Lead) => (
        <div>
          <div style={getLeadCategoryStyle(record.lead_category)}>
            {name}
            {/* Show user icon for team leaders' own leads */}
             {user && (user.role === 'team_leader' || user.role === 'admin' || user.role === 'super_admin') && 
              record.assigned_to === getUserIdAsNumber(user) && (
              <span style={{ marginLeft: '8px', fontSize: '14px' }}>👤</span>
            )}
          </div>
          {record.company && <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)' }}>{record.company}</div>}
        </div>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (email: string) => email || <span style={{ color: 'rgba(255, 255, 255, 0.6)', fontStyle: 'italic' }}>Não disponível</span>,
    },
    {
      title: 'Telefone',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'NIF',
      dataIndex: 'dif',
      key: 'dif',
    },
    {
      title: 'Tipo de Cliente',
      dataIndex: 'customer_type',
      key: 'customer_type',
      render: (customerType: string) => {
        const isResidential = customerType === 'residential';
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {isResidential ? (
              <HomeOutlined style={{ fontSize: '18px', color: '#60a5fa' }} title="Residencial" />
            ) : (
              <ShopOutlined style={{ fontSize: '18px', color: '#34d399' }} title="Empresarial" />
            )}
          </div>
        );
      },
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => {
        const customerTypeOptions = [
          { text: 'Residencial', value: 'residential', icon: <HomeOutlined style={{ marginRight: 8 }} /> },
          { text: 'Empresarial', value: 'business', icon: <ShopOutlined style={{ marginRight: 8 }} /> },
        ];
        
        const handleSelectAll = () => {
          setSelectedKeys(customerTypeOptions.map(option => option.value));
        };
        
        return (
          <div style={{ padding: 8 }}>
            <div style={{ marginBottom: 8 }}>
              {customerTypeOptions.map(option => (
                <div key={option.value} style={{ marginBottom: 4 }}>
                  <label style={{ display: 'flex', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={selectedKeys.includes(option.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedKeys([...selectedKeys, option.value]);
                        } else {
                          setSelectedKeys(selectedKeys.filter((key: string) => key !== option.value));
                        }
                      }}
                      style={{ marginRight: 8 }}
                    />
                    {option.icon}
                    {option.text}
                  </label>
                </div>
              ))}
            </div>
            <div style={{ borderTop: '1px solid #d9d9d9', paddingTop: 8, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                size="small"
                onClick={handleSelectAll}
              >
                Selecionar Todos
              </Button>
              <div>
                <Button
                  size="small"
                  onClick={() => clearFilters && clearFilters()}
                  style={{ marginRight: 8 }}
                >
                  Reset
                </Button>
                <Button
                  type="primary"
                  size="small"
                  onClick={() => confirm()}
                >
                  OK
                </Button>
              </div>
            </div>
          </div>
        );
      },
      onFilter: (value: boolean | React.Key, record: Lead) => record.customer_type === String(value),
    },
    // Only show Área column for admin users
    ...(user?.role === 'admin' ? [{
      title: 'Área',
      dataIndex: 'business_area',
      key: 'business_area',
      render: (area: string | string[]) => {
        const areas = Array.isArray(area) ? area : [area];
        return areas.map(a => (
          <Tag color={getAreaColor(a)} key={a}>
            {a === 'telecommunications' ? 'Telecomunicações' : 'Energia'}
        </Tag>
        ));
      },
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => {
        const areaOptions = [
          { text: 'Telecomunicações', value: 'telecommunications' },
          { text: 'Energia', value: 'energy_solutions' },
        ];
        
        const handleSelectAll = () => {
          setSelectedKeys(areaOptions.map(option => option.value));
        };
        
        return (
          <div style={{ padding: 8 }}>
            <div style={{ marginBottom: 8 }}>
              {areaOptions.map(option => (
                <div key={option.value} style={{ marginBottom: 4 }}>
                  <label style={{ display: 'flex', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={selectedKeys.includes(option.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedKeys([...selectedKeys, option.value]);
                        } else {
                          setSelectedKeys(selectedKeys.filter((key: string) => key !== option.value));
                        }
                      }}
                      style={{ marginRight: 8 }}
                    />
                    {option.text}
                  </label>
                </div>
              ))}
            </div>
            <div style={{ borderTop: '1px solid #d9d9d9', paddingTop: 8, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                size="small"
                onClick={handleSelectAll}
              >
                Selecionar Todos
              </Button>
              <div>
                <Button
                  size="small"
                  onClick={() => clearFilters && clearFilters()}
                  style={{ marginRight: 8 }}
                >
                  Reset
                </Button>
                <Button
                  type="primary"
                  size="small"
                  onClick={() => confirm()}
                >
                  OK
                </Button>
              </div>
            </div>
          </div>
        );
      },
      onFilter: (value: boolean | React.Key, record: Lead) => {
        const areas = Array.isArray(record.business_area) ? record.business_area : [record.business_area];
        return areas.includes(String(value));
      },
    }] : []),
    {
      title: 'Estado/Etapa',
      dataIndex: 'lead_status',
      key: 'lead_status',
      render: (status: string, record: Lead) => {
        // Always show the actual status with proper colors
        const displayStatus = status || 'Por definir';
        const color = getStatusColor(displayStatus);
        
        // Special styling for "Não Atribuido"
        if (displayStatus === 'Não Atribuido') {
          return (
            <Tag style={{ backgroundColor: '#000', color: '#fff', borderColor: '#000' }}>
              {displayStatus}
            </Tag>
          );
        }
        
        return (
          <Tag color={color}>
            {displayStatus}
          </Tag>
        );
      },
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => {
        // Get all unique status values from the current data
        const uniqueStatuses = Array.from(new Set(leads.map(lead => lead.lead_status).filter(Boolean)));
        
        const handleSelectAll = () => {
          setSelectedKeys(uniqueStatuses);
        };
        
        return (
          <div style={{ padding: 8 }}>
            <div style={{ marginBottom: 8 }}>
              {uniqueStatuses.map(status => (
                <div key={status} style={{ marginBottom: 4 }}>
                  <label style={{ display: 'flex', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={selectedKeys.includes(status)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedKeys([...selectedKeys, status]);
                        } else {
                          setSelectedKeys(selectedKeys.filter((key: string) => key !== status));
                        }
                      }}
                      style={{ marginRight: 8 }}
                    />
                    {status}
                  </label>
                </div>
              ))}
            </div>
            <div style={{ borderTop: '1px solid #d9d9d9', paddingTop: 8, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                size="small"
                onClick={handleSelectAll}
              >
                Selecionar Todos
              </Button>
              <div>
                <Button
                  size="small"
                  onClick={() => clearFilters && clearFilters()}
                  style={{ marginRight: 8 }}
                >
                  Reset
                </Button>
                <Button
                  type="primary"
                  size="small"
                  onClick={() => confirm()}
                >
                  OK
                </Button>
              </div>
            </div>
          </div>
        );
      },
      onFilter: (value: boolean | React.Key, record: Lead) => record.lead_status === String(value),
    },
    {
      title: 'Atribuído a',
      dataIndex: 'assigned_to',
      key: 'assigned_to',
      render: (assignedToId: number) => {
        const assignedUser = availableUsers.find(u => u.id === assignedToId);
        return assignedUser ? assignedUser.username : '-';
      },
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => {
        const handleSelectAll = () => {
          setSelectedKeys(availableUsers.map(user => user.id.toString()));
        };
        
        const handleSelectSelf = () => {
          if (user) {
            setSelectedKeys([user.id.toString()]);
          }
        };
        
        return (
          <div style={{ padding: 8 }}>
            <div style={{ marginBottom: 8 }}>
              {/* Self filter option */}
              {user && (
                <div style={{ marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid #f0f0f0' }}>
                  <Button
                    size="small"
                    type="dashed"
                    onClick={handleSelectSelf}
                    style={{ width: '100%', fontSize: 12 }}
                  >
                    Apenas os meus leads
                  </Button>
                </div>
              )}
              
              {availableUsers.map(user => (
                <div key={user.id} style={{ marginBottom: 4 }}>
                  <label style={{ display: 'flex', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={selectedKeys.includes(user.id.toString())}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedKeys([...selectedKeys, user.id.toString()]);
                        } else {
                          setSelectedKeys(selectedKeys.filter((key: string) => key !== user.id.toString()));
                        }
                      }}
                      style={{ marginRight: 8 }}
                    />
                    {user.username}
                  </label>
                </div>
              ))}
            </div>
            <div style={{ borderTop: '1px solid #d9d9d9', paddingTop: 8, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                size="small"
                onClick={handleSelectAll}
              >
                Selecionar Todos
              </Button>
              <div>
                <Button
                  size="small"
                  onClick={() => clearFilters && clearFilters()}
                  style={{ marginRight: 8 }}
                >
                  Reset
                </Button>
                <Button
                  type="primary"
                  size="small"
                  onClick={() => confirm()}
                >
                  OK
                </Button>
              </div>
            </div>
          </div>
        );
      },
      onFilter: (value: boolean | React.Key, record: Lead) => {
        return record.assigned_to?.toString() === String(value);
      },
      sorter: (a: Lead, b: Lead) => {
        const aUser = availableUsers.find(u => u.id === a.assigned_to);
        const bUser = availableUsers.find(u => u.id === b.assigned_to);
        const aName = aUser ? aUser.username : '';
        const bName = bUser ? bUser.username : '';
        
        // Unassigned leads (empty names) should come first
        if (!aName && bName) return -1;
        if (aName && !bName) return 1;
        if (!aName && !bName) return 0;
        
        // Then sort alphabetically
        return aName.localeCompare(bName);
      },
    },
    {
      title: 'Criado em',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => dayjs(date).format('DD/MM/YYYY HH:mm'),
      sorter: (a: Lead, b: Lead) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    },
    {
      title: 'Data da Instalação',
      dataIndex: 'installation_date',
      key: 'installation_date',
      render: (date: string, record: Lead) => {
        // Only show for leads in 'instalados' section
        if (record.section !== 'instalados') {
          return <span style={{ color: '#999', fontStyle: 'italic' }}>-</span>;
        }
        
        if (date) {
          return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <CalendarOutlined style={{ marginRight: '4px', color: '#60a5fa' }} />
              {dayjs(date).format('DD/MM/YYYY')}
            </div>
          );
        }
        
        return (
          <span style={{ color: 'rgba(255, 255, 255, 0.6)', fontStyle: 'italic' }}>Selecionar data</span>
        );
      },
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (_: any, record: Lead) => (
        <Space>
          <Dropdown
            menu={{
              items: [
                {
                  key: 'view',
                  label: (
                    <button 
                      type="button" 
                      onClick={() => {
                        setSelectedLeadForDetails(record);
                        setLeadDetailsModalVisible(true);
                      }}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', width: '100%', textAlign: 'left' }}
                    >
                  Ver Detalhes
                    </button>
                  ),
                  icon: <EyeOutlined />
                },
                {
                  key: 'edit',
                  label: (
                    <button 
                      type="button" 
                      onClick={() => showModal(record)}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', width: '100%', textAlign: 'left' }}
                    >
                  Editar
                    </button>
                  ),
                  icon: <EditOutlined />
                },
                {
                  key: 'assign',
                  label: (
                    <button 
                      type="button" 
                      onClick={() => { setSelectionMode('assign'); setSelectedRowKeys([record.id]); }}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', width: '100%', textAlign: 'left' }}
                    >
                  Reatribuir
                    </button>
                  ),
                  icon: <UserAddOutlined />
                },
                {
                  key: 'delete',
                  label: (
                    <Popconfirm
                      title="Confirmar exclusão"
                      onConfirm={() => handleDelete(record.id)}
                      okText="Sim"
                      cancelText="Cancelar"
                >
                  Excluir
                    </Popconfirm>
                  ),
                  icon: <DeleteOutlined />,
                  danger: true
                }
              ]
            }}
            trigger={['click']}
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        </Space>
      ),
    },
  ];

  // Business area specific form fields
  const getFormFields = (currentCustomerType: string = 'residential') => {
    const baseFields: Array<{
      name: string;
      label: string;
      rules?: any[];
      component: JSX.Element;
    }> = [];

    // Nome (sempre obrigatório)
      baseFields.push(
        {
          name: 'name',
          label: 'Nome ou Denominação social',
          rules: [{ required: true, message: 'Por favor insira o nome!' }],
          component: <Input placeholder="Nome ou denominação social da lead" />
        }
      );

    // Email e Telefone
    baseFields.push(
      {
        name: 'email',
        label: 'Email',
        rules: [
          { required: true, message: 'Por favor insira o email!' },
          { type: 'email' as const, message: 'Por favor insira um email válido!' }
        ],
        component: <Input placeholder="email@exemplo.com" />
      },
      {
        name: 'phone',
        label: 'Telefone',
        rules: [{ required: true, message: 'Por favor insira o telefone!' }],
        component: <Input placeholder="+351 123 456 789" />
      }
    );

    // NIF
    if (currentBusinessArea === 'telecommunications') {
      baseFields.push({
          name: 'dif',
          label: 'NIF',
          rules: [{ required: true, message: 'Por favor insira o NIF!' }],
          component: <Input placeholder="123456789" />
      });
    } else {
      baseFields.push({
        name: 'dif',
        label: 'NIF',
        rules: [],
        component: <Input placeholder="123456789 (opcional)" />
      });
    }

    // Operador Atual (Telecom)
    if (currentBusinessArea === 'telecommunications') {
      baseFields.push({
          name: 'current_provider',
          label: 'Operador Atual',
          component: <Input placeholder="Ex: MEO, NOS, Vodafone" />
      });
        }

    // Morada, Cidade
      baseFields.push(
        {
          name: 'address',
          label: 'Morada',
        rules: [],
          component: <Input placeholder="Rua, número, andar" />
        },
        {
          name: 'city',
          label: 'Cidade',
        rules: [],
          component: <Input placeholder="Cidade" />
        }
      );

    // Código Postal (2 campos)
    baseFields.push(
      {
        name: 'postal_code_4',
        label: 'Código Postal (4 dígitos)',
        rules: [
          { required: true, message: 'Por favor insira os primeiros 4 dígitos!' },
          { pattern: /^\d{4}$/, message: 'Deve ter exatamente 4 dígitos numéricos!' }
        ],
        component: <Input maxLength={4} placeholder="1000" />
      },
      {
        name: 'postal_code_3',
        label: 'Código Postal (3 dígitos)',
        rules: [
          { required: true, message: 'Por favor insira os últimos 3 dígitos!' },
          { pattern: /^\d{3}$/, message: 'Deve ter exatamente 3 dígitos numéricos!' }
        ],
        component: <Input maxLength={3} placeholder="123" />
      }
    );

    // Customer type
    baseFields.push({
        name: 'customer_type',
        label: 'Tipo de Cliente',
        rules: [{ required: true, message: 'Por favor selecione o tipo!' }],
        component: (
          <Select 
          placeholder="Selecione o tipo de cliente"
          onChange={() => setFormKey(prev => prev + 1)}
          >
            <Option value="residential">Residencial</Option>
            <Option value="business">Empresarial</Option>
          </Select>
        )
    });

    // Estado/Etapa (sempre Entrada, disabled)
    baseFields.push({
        name: 'lead_status',
      label: 'Estado/Etapa',
      rules: [{ required: true, message: 'Por favor selecione o estado/etapa!' }],
        component: (
        <Select disabled value="Entrada">
          <Option value="Entrada">Entrada</Option>
          </Select>
        )
    });

    return baseFields;
  };

  return (
    <div className="leads-management-container">
      <style>{duplicateLeadStyles}</style>
      <Card
        bodyStyle={{ padding: '24px', minHeight: 400, background: 'transparent' }}
        style={{ width: '100%', boxShadow: '0 4px 16px rgba(0,0,0,0.1)', borderRadius: '12px', background: 'rgba(255, 255, 255, 0.15)', backdropFilter: 'blur(10px)' }}
      >
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <Title level={3} style={{ margin: 0, marginBottom: '8px', fontSize: 28, fontWeight: 700, color: '#ffffff' }}>
            Gestão de Leads
          </Title>
          <Text type="secondary" style={{ fontSize: 14, color: 'rgba(255, 255, 255, 0.8)' }}>
            {currentBusinessArea === 'telecommunications' ? 'Telecomunicações' : 'Energia'} - {filteredLeads.length} leads
            {searchText && ` (${leads.length} total)`}
          </Text>
        </div>

        {/* Search and Action Buttons */}
        <Row gutter={16} style={{ marginBottom: '24px' }}>
          <Col span={24}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              {/* Left side: Search Bar */}
              <div style={{ flex: '0 0 auto', minWidth: '300px', maxWidth: '400px' }}>
                <Input.Search
                  placeholder="Pesquisar leads..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onSearch={setSearchText}
                  className="modern-search-input"
                  style={{ width: '100%' }}
                  prefix={<SearchOutlined style={{ color: 'rgba(255, 255, 255, 0.9)' }} />}
                  size="small"
                  allowClear
                  enterButton={false}
                />
              </div>
              
              {/* Right side: Action Buttons */}
              <Space size="middle" wrap>
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  onClick={() => setImportModalVisible(true)}
                  loading={importing}
                  style={{ fontSize: 14, height: '36px', borderRadius: '8px', fontWeight: 500 }}
                >
                  Importar
                </Button>
                <Button 
                  type="primary" 
                  icon={<PlusOutlined />} 
                  onClick={() => showModal()}
                  style={{ fontSize: 14, height: '36px', borderRadius: '8px', fontWeight: 500 }}
                >
                  Novo Lead
                </Button>
                <Button 
                  icon={<UserAddOutlined />} 
                  onClick={() => { setSelectionMode('assign'); setSelectedRowKeys([]); }}
                  style={{ fontSize: 14, height: '36px', borderRadius: '8px', fontWeight: 500 }}
                >
                  Atribuir
                </Button>
                {selectionMode === 'assign' && selectedRowKeys.length > 0 && (
                  <Button 
                    type="primary" 
                    onClick={() => setShowAssignmentModal(true)}
                    style={{ fontSize: 14, height: '36px', borderRadius: '8px', fontWeight: 500 }}
                  >
                    Atribuir Selecionados ({selectedRowKeys.length})
                  </Button>
                )}
                {selectionMode === 'assign' && (
                  <Button 
                    onClick={() => { 
                      setSelectionMode('none'); 
                      setSelectedRowKeys([]); 
                      setShowAssignmentModal(false);
                    }}
                    style={{ fontSize: 14, height: '36px', borderRadius: '8px', fontWeight: 500 }}
                  >
                    Cancelar
                  </Button>
                )}
                {user?.role === 'admin' && (
                  <Button 
                    icon={<ShareAltOutlined />} 
                    onClick={() => { setSelectionMode('share'); setSelectedRowKeys([]); }}
                    style={{ fontSize: 14, height: '36px', borderRadius: '8px', fontWeight: 500 }}
                  >
                    Partilhar
                  </Button>
                )}
                {selectionMode === 'share' && selectedRowKeys.length > 0 && (
                  <Button 
                    type="primary" 
                    onClick={() => {
                      // Show share modal logic here
                      // For now, we'll use the existing modal condition
                    }}
                    style={{ fontSize: 14, height: '36px', borderRadius: '8px', fontWeight: 500 }}
                  >
                    Partilhar Selecionados ({selectedRowKeys.length})
                  </Button>
                )}
                {selectionMode === 'share' && (
                  <Button 
                    onClick={() => { 
                      setSelectionMode('none'); 
                      setSelectedRowKeys([]); 
                    }}
                    style={{ fontSize: 14, height: '36px', borderRadius: '8px', fontWeight: 500 }}
                  >
                    Cancelar
                  </Button>
                )}
                <Button 
                  danger 
                  icon={<DeleteOutlined />} 
                  onClick={() => { setSelectionMode('delete'); setSelectedRowKeys([]); }}
                  style={{ fontSize: 14, height: '36px', borderRadius: '8px', fontWeight: 500 }}
                >
                  Eliminar
                </Button>
                {selectionMode === 'delete' && selectedRowKeys.length > 0 && (
                  <Popconfirm
                    title={`Tem a certeza que deseja eliminar ${selectedRowKeys.length} leads? Esta ação não pode ser desfeita.`}
                    onConfirm={handleBatchDelete}
                    okText="Sim, eliminar"
                    cancelText="Cancelar"
                  >
                    <Button 
                      type="primary" 
                      danger
                      style={{ fontSize: 14, height: '36px', borderRadius: '8px', fontWeight: 500 }}
                    >
                      Eliminar Selecionados ({selectedRowKeys.length})
                    </Button>
                  </Popconfirm>
                )}
                {selectionMode === 'delete' && (
                  <Button 
                    onClick={() => { 
                      setSelectionMode('none'); 
                      setSelectedRowKeys([]); 
                    }}
                    style={{ fontSize: 14, height: '36px', borderRadius: '8px', fontWeight: 500 }}
                  >
                    Cancelar
                  </Button>
                )}
                {/* Temporary: Show button for all users for testing */}
                <Button 
                  type="default" 
                  danger={duplicateStats?.pending > 0}
                  icon={<WarningOutlined />}
                  onClick={() => setShowDuplicateModal(true)}
                  style={{ fontSize: 14, height: '36px', borderRadius: '8px', fontWeight: 500 }}
                >
                  Duplicados e Histórico {duplicateStats?.pending > 0 && `(${duplicateStats.pending})`}
                </Button>
              </Space>
            </div>
          </Col>
        </Row>

        {/* Table */}
        <div style={{ marginTop: '24px' }}>
        {selectionMode === 'assign' && (
          <Alert
            message="Modo de Seleção Ativo"
            type="info"
            showIcon
            style={{ marginBottom: 16, background: 'rgba(255, 255, 255, 0.15)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.3)', borderRadius: '8px' }}
            action={
              <Button 
                size="small" 
                onClick={() => { 
                  setSelectionMode('none'); 
                  setSelectedRowKeys([]); 
                  setShowAssignmentModal(false);
                }}
                style={{ fontSize: 13, borderRadius: '6px', height: '32px' }}
              >
                Cancelar
              </Button>
            }
          />
        )}
        {selectionMode === 'share' && (
          <Alert
            message="Modo de Partilha Ativo"
            description={`Selecione os leads que deseja partilhar. ${selectedRowKeys.length > 0 ? `(${selectedRowKeys.length} selecionados)` : ''}`}
            type="info"
            showIcon
            style={{ marginBottom: 16, background: 'rgba(255, 255, 255, 0.15)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.3)', borderRadius: '8px' }}
            action={
              <Button 
                size="small" 
                onClick={() => { 
                  setSelectionMode('none'); 
                  setSelectedRowKeys([]); 
                }}
                style={{ fontSize: 13, borderRadius: '6px', height: '32px' }}
              >
                Cancelar
              </Button>
            }
          />
        )}
        {selectionMode === 'delete' && (
          <Alert
            message="Modo de Eliminação Ativo"
            description={`Selecione os leads que deseja eliminar. ${selectedRowKeys.length > 0 ? `(${selectedRowKeys.length} selecionados)` : ''}`}
            type="warning"
            showIcon
            style={{ marginBottom: 16, background: 'rgba(255, 255, 255, 0.15)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.3)', borderRadius: '8px' }}
            action={
              <Button 
                size="small" 
                onClick={() => { 
                  setSelectionMode('none'); 
                  setSelectedRowKeys([]); 
                }}
                style={{ fontSize: 13, borderRadius: '6px', height: '32px' }}
              >
                Cancelar
              </Button>
            }
          />
        )}
        <Table
          key={`leads-${filteredLeads.length}`}
          columns={columns}
          dataSource={filteredLeads}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1200 }}
          pagination={{
            pageSize: pageSize,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} leads`,
            onShowSizeChange: (current, size) => setPageSize(size),
            locale: {
              items_per_page: ' / página',
              jump_to: 'Ir para',
              jump_to_confirm: 'confirmar',
              page: 'Página',
              prev_page: 'Página anterior',
              next_page: 'Próxima página',
              prev_5: '5 páginas anteriores',
              next_5: '5 páginas seguintes',
              prev_3: '3 páginas anteriores',
              next_3: '3 páginas seguintes',
            }
          }}
          rowSelection={selectionMode !== 'none' ? {
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys as number[]),
          } : undefined}
          locale={{
            emptyText: 'Nenhum lead encontrado'
          }}
        />
        </div>
      </Card>

      {/* Lead Form Modal */}
      <Modal
        title={editingLead ? 'Editar Lead' : 'Nova Lead'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={handleModalOk}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          key={formKey} // Force re-render when customer type changes
        >
          {getFormFields(form.getFieldValue('customer_type') || 'residential').map(field => (
            <Form.Item
              key={field.name}
              name={field.name}
              label={field.label}
              rules={field.rules}
            >
              {field.component}
            </Form.Item>
          ))}
        </Form>
      </Modal>

      {/* New Import Modal */}
      <Modal
        title="Importar Leads"
        open={importModalVisible}
        onCancel={() => {
          setImportModalVisible(false);
          setSelectedImportType(null);
          setImportFiles({ intra: null, '4000Junho': null, leads: null });
          setPreviewData({ intra: [], '4000Junho': [], leads: [] });
          setLeadCategory(0);
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setImportModalVisible(false);
            setSelectedImportType(null);
            setImportFiles({ intra: null, '4000Junho': null, leads: null });
            setPreviewData({ intra: [], '4000Junho': [], leads: [] });
            setLeadCategory(0);
          }}>
            Cancelar
          </Button>,
          <Button 
            key="import" 
            type="primary" 
            onClick={handleImportSubmit}
            loading={importing}
            disabled={!selectedImportType || !importFiles[selectedImportType as keyof typeof importFiles]}
          >
            Importar
          </Button>
        ]}
        width={1000}
      >
        {/* Lead Category Selection */}
        <div style={{ marginBottom: 24, padding: 16, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
          <Text strong style={{ marginBottom: 8, display: 'block' }}>
            Categoria Inicial dos Leads:
          </Text>
          <Select
            value={leadCategory}
            onChange={setLeadCategory}
            style={{ width: 300 }}
            placeholder="Selecione a categoria inicial"
          >
            <Option value={0}>Branco</Option>
            <Option value={2}>Azul</Option>
            <Option value={3}>Amarelo</Option>
            <Option value={4}>Vermelho</Option>
          </Select>
          <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
            Esta categoria será aplicada a todos os leads importados. A cor do nome do lead será baseada nesta categoria.
          </div>
        </div>
        
        <Row gutter={[16, 16]}>
          {/* Intra Option */}
          <Col span={8}>
            <Card 
              title="Intra" 
              style={{ 
                height: '100%',
                border: selectedImportType === 'intra' ? '2px solid #1890ff' : '1px solid #d9d9d9'
              }}
            >
              <div style={{ marginBottom: 16 }}>
                <Text type="secondary">
                  Colunas esperadas: A (Nome), B (Contacto)
                </Text>
              </div>
              
              <Upload
                accept=".xlsx,.xls"
                showUploadList={false}
                beforeUpload={(file) => {
                  handleFileUpload('intra', file);
                  return false;
                }}
                disabled={importing}
              >
                <Button 
                  icon={<UploadOutlined />} 
                  block
                  disabled={importing}
                >
                  Selecionar ficheiro
                </Button>
              </Upload>
              
              {importFiles.intra && (
                <div style={{ marginTop: 16 }}>
                  <Text strong>Arquivo: {importFiles.intra?.name}</Text>
                </div>
              )}
              
              {previewLoading.intra && (
                <div style={{ marginTop: 16, textAlign: 'center' }}>
                  <Spin size="small" />
                  <div>Gerando preview...</div>
                </div>
              )}
              
              {previewData.intra.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <Text strong>Preview (primeiras 5 linhas):</Text>
                  <div style={{ 
                    maxHeight: 200, 
                    overflow: 'auto', 
                    border: '1px solid #d9d9d9', 
                    padding: 8, 
                    marginTop: 8,
                    backgroundColor: '#fafafa'
                  }}>
                    {previewData.intra.map((row, index) => (
                      <div key={index} style={{ fontSize: '12px', marginBottom: 4 }}>
                        {JSON.stringify(row)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          </Col>

          {/* 4000Junho Option */}
          <Col span={8}>
            <Card 
              title="4000Junho" 
              style={{ 
                height: '100%',
                border: selectedImportType === '4000Junho' ? '2px solid #1890ff' : '1px solid #d9d9d9'
              }}
            >
              <div style={{ marginBottom: 16 }}>
                <Text type="secondary">
                  Colunas esperadas: A (Informações em massa), B (Data de fim de fidelização)
                </Text>
              </div>
              
              <Upload
                accept=".xlsx,.xls"
                showUploadList={false}
                beforeUpload={(file) => {
                  handleFileUpload('4000Junho', file);
                  return false;
                }}
                disabled={importing}
              >
                <Button 
                  icon={<UploadOutlined />} 
                  block
                  disabled={importing}
                >
                  Selecionar ficheiro
                </Button>
              </Upload>
              
              {importFiles['4000Junho'] && (
                <div style={{ marginTop: 16 }}>
                  <Text strong>Arquivo: {importFiles['4000Junho']?.name}</Text>
                </div>
              )}
              
              {previewLoading['4000Junho'] && (
                <div style={{ marginTop: 16, textAlign: 'center' }}>
                  <Spin size="small" />
                  <div>Gerando preview...</div>
                </div>
              )}
              
              {previewData['4000Junho'].length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <Text strong>Preview (primeiras 5 linhas):</Text>
                  <div style={{ 
                    maxHeight: 200, 
                    overflow: 'auto', 
                    border: '1px solid #d9d9d9', 
                    padding: 8, 
                    marginTop: 8,
                    backgroundColor: '#fafafa'
                  }}>
                    {previewData['4000Junho'].map((row, index) => (
                      <div key={index} style={{ fontSize: '12px', marginBottom: 4 }}>
                        {JSON.stringify(row)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          </Col>

          {/* Leads Option */}
          <Col span={8}>
            <Card 
              title="Leads" 
              style={{ 
                height: '100%',
                border: selectedImportType === 'leads' ? '2px solid #1890ff' : '1px solid #d9d9d9'
              }}
            >
              <div style={{ marginBottom: 16 }}>
                <Text type="secondary">
                  Colunas esperadas: B (Nome), C (Telefone), D (Informações detalhadas com NIF, CC, fim de fidelização)
                </Text>
              </div>
              
              <Upload
                accept=".xlsx,.xls"
                showUploadList={false}
                beforeUpload={(file) => {
                  handleFileUpload('leads', file);
                  return false;
                }}
                disabled={importing}
              >
                <Button 
                  icon={<UploadOutlined />} 
                  block
                  disabled={importing}
                >
                  Selecionar ficheiro
                </Button>
              </Upload>
              
              {importFiles.leads && (
                <div style={{ marginTop: 16 }}>
                  <Text strong>Arquivo: {importFiles.leads?.name}</Text>
                </div>
              )}
              
              {previewLoading.leads && (
                <div style={{ marginTop: 16, textAlign: 'center' }}>
                  <Spin size="small" />
                  <div>Gerando preview...</div>
                </div>
              )}
              
              {previewData.leads.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <Text strong>Preview (primeiras 5 linhas):</Text>
                  <div style={{ 
                    maxHeight: 200, 
                    overflow: 'auto', 
                    border: '1px solid #d9d9d9', 
                    padding: 8, 
                    marginTop: 8,
                    backgroundColor: '#fafafa'
                  }}>
                    {previewData.leads.map((row, index) => (
                      <div key={index} style={{ fontSize: '12px', marginBottom: 4 }}>
                        {JSON.stringify(row)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </Modal>

      {/* Assignment History Modal */}
      <Modal
        title="Histórico de Atribuição"
        open={false} // Removed historyVisible state
        onCancel={() => {}} // Removed historyVisible state
        footer={null}
        width={600}
      >
        {/* Removed historyLoading state */}
        {/* Removed assignmentHistory state */}
        {/* Removed List component for assignment history */}
      </Modal>

      {/* Batch Assign/Share Modal */}
      {showAssignmentModal && (
        <Modal
          title="Atribuir leads"
          open={true}
          onOk={handleBatchAssign}
          onCancel={() => { 
            setShowAssignmentModal(false); 
            setSelectionMode('none'); 
            setSelectedRowKeys([]); 
          }}
          okText="Atribuir"
          cancelText="Cancelar"
        >
          <Form layout="vertical">
            <Form.Item label="Responsável">
              <Select
                value={batchResponsible}
                onChange={setBatchResponsible}
                placeholder="Selecione o responsável"
              >
                {availableUsers.map(u => (
                  <Option key={u.id} value={u.id}>{u.username}</Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        </Modal>
      )}
      {selectionMode === 'share' && selectedRowKeys.length > 0 && user?.role === 'admin' && (
        <Modal
          title="Partilhar leads"
          open={true}
          onOk={handleBatchShare}
          onCancel={() => { setSelectionMode('none'); setSelectedRowKeys([]); }}
          okText="Partilhar"
          cancelText="Cancelar"
        >
          <Form layout="vertical">
            <Form.Item label="Áreas de negócio para partilhar">
              <Select
                mode="multiple"
                value={batchShareAreas}
                onChange={setBatchShareAreas}
                placeholder="Selecione as áreas"
              >
                <Option value="telecommunications">Telecomunicações</Option>
                <Option value="energy_solutions">Energia</Option>
              </Select>
            </Form.Item>
            <Form.Item label="Utilizadores para partilhar">
              <Select
                mode="multiple"
                value={batchShareUsers}
                onChange={setBatchShareUsers}
                placeholder="Selecione os utilizadores"
              >
                {availableUsers.map(u => (
                  <Option key={u.id} value={u.id}>{u.username}</Option>
                ))}
              </Select>
            </Form.Item>
        </Form>
      </Modal>
      )}

      {/* Duplicate Leads Modal */}
      <Modal
        title="Duplicados e Histórico de Importações"
        open={showDuplicateModal}
        onCancel={() => {
          setShowDuplicateModal(false);
          setDuplicateSelectionMode(false);
          setSelectedDuplicateKeys([]);
        }}
        footer={null}
        width={1400}
        className={deletingDuplicates ? 'batch-operating' : ''}
      >
        <Tabs
          defaultActiveKey="duplicates"
          items={[
            {
              key: 'duplicates',
              label: `Duplicados ${duplicateStats?.pending > 0 ? `(${duplicateStats.pending})` : ''}`,
              children: (
                <div>
                  {/* Batch Selection Controls */}
                  <div style={{ marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Button
                      type={duplicateSelectionMode ? 'primary' : 'default'}
                      onClick={() => setDuplicateSelectionMode(!duplicateSelectionMode)}
                    >
                      {duplicateSelectionMode ? 'Cancelar Seleção' : 'Selecionar para Eliminar'}
                    </Button>
                    
                    {duplicateSelectionMode && (
                      <>
                        <Button
                          onClick={() => setSelectedDuplicateKeys(duplicateLeads.map(lead => lead.id))}
                        >
                          Selecionar Todos
                        </Button>
                        
                        {selectedDuplicateKeys.length > 0 && (
                          <Popconfirm
                            title={`Eliminar ${selectedDuplicateKeys.length} leads duplicados?`}
                            onConfirm={handleBatchDeleteDuplicates}
                            okText="Sim, eliminar"
                            cancelText="Cancelar"
                          >
                            <Button 
                              danger 
                              loading={deletingDuplicates}
                            >
                              Eliminar Selecionados ({selectedDuplicateKeys.length})
                            </Button>
                          </Popconfirm>
                        )}
                      </>
                    )}
                  </div>

                  {duplicateSelectionMode && selectedDuplicateKeys.length > 0 && (
                    <Alert
                      message={`Modo de Seleção Ativo - ${selectedDuplicateKeys.length} selecionados`}
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />
                  )}

                  {duplicateStats?.pending > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <Alert
                        message="Leads Duplicados Pendentes"
                        description={`Existem ${duplicateStats?.pending || 0} leads duplicados aguardando resolução.`}
                        type="warning"
                        showIcon
                      />
                    </div>
                  )}

                  <Table
                    key={`duplicates-${duplicateLeads.length}`}
                    dataSource={duplicateLeads}
                    rowKey="id"
                    loading={deletingDuplicates}
                    rowSelection={duplicateSelectionMode ? {
                      selectedRowKeys: selectedDuplicateKeys,
                      onChange: (keys) => setSelectedDuplicateKeys(keys),
                    } : undefined}
                    columns={[
                      {
                        title: 'Lead Importado',
                        dataIndex: 'name',
                        key: 'name',
                        render: (name: string, record: any) => (
                          <div>
                            <div><strong>{name}</strong></div>
                            <div style={{ fontSize: '12px', color: '#666' }}>
                              Telefone: {record.phone} | Email: {record.email}
                            </div>
                          </div>
                        )
                      },
                      {
                        title: 'Lead Existente',
                        dataIndex: 'existing_lead',
                        key: 'existing_lead',
                        render: (existingLead: any) => (
                          existingLead ? (
                            <div>
                              <div><strong>{existingLead.name}</strong></div>
                              <div style={{ fontSize: '12px', color: '#666' }}>
                                Telefone: {existingLead.phone} | Email: {existingLead.email}
                              </div>
                            </div>
                          ) : <Text type="secondary">N/A</Text>
                        )
                      },
                      {
                        title: 'Status',
                        dataIndex: 'duplicate_status',
                        key: 'duplicate_status',
                        render: (status: string) => (
                          <Tag color={status === 'pending' ? 'orange' : 'green'}>
                            {status === 'pending' ? 'Pendente' : 'Resolvido'}
                          </Tag>
                        )
                      },
                      {
                        title: 'Ações',
                        key: 'actions',
                        render: (_, record: any) => (
                          <Space>
                            <Popconfirm
                              title="Tem a certeza que pretende eliminar este lead duplicado?"
                              onConfirm={() => handleResolveDuplicate(record.id, 'delete')}
                              okText="Sim"
                              cancelText="Não"
                            >
                              <Button size="small" danger loading={resolvingDuplicate}>
                                Eliminar
                              </Button>
                            </Popconfirm>
                            <Button 
                              size="small" 
                              type="primary"
                              onClick={() => handleResolveDuplicate(record.id, 'edit', {
                                name: record.name,
                                email: record.email,
                                phone: record.phone
                              })}
                              loading={resolvingDuplicate}
                            >
                              Editar e Resolver
                            </Button>
                            {record.existing_lead && (
                              <Button 
                                size="small"
                                onClick={() => handleResolveDuplicate(record.id, 'merge', {
                                  name: record.name,
                                  first_name: record.first_name,
                                  last_name: record.last_name,
                                  email: record.email,
                                  phone: record.phone,
                                  company: record.company,
                                  job_title: record.job_title,
                                  dif: record.dif,
                                  address: record.address,
                                  city: record.city,
                                  postal_code: record.postal_code,
                                  country: record.country,
                                  current_provider: record.current_provider,
                                  contract_end_date: record.contract_end_date,
                                  end_of_contract: record.end_of_contract,
                                  monthly_spend: record.monthly_spend,
                                  service_interest: record.service_interest,
                                  business_area: record.business_area,
                                  customer_type: record.customer_type,
                                  lead_source: record.lead_source,
                                  tags: record.tags
                                })}
                                loading={resolvingDuplicate}
                              >
                                Unir
                              </Button>
                            )}
                          </Space>
                        )
                      }
                    ]}
                    pagination={{
                      pageSize: 10,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} duplicados`
                    }}
                  />
                </div>
              )
            },
            {
              key: 'audit',
              label: `Histórico de Importações (${auditLogs.length})`,
              children: (
                <div>
                  {/* Clear History Button */}
                  <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <Text type="secondary">
                        Total de registos: {auditLogs.length}
                      </Text>
                    </div>
                    <Popconfirm
                      title="Esta ação irá eliminar permanentemente todos os registos de histórico de importações. Esta ação não pode ser desfeita."
                      onConfirm={handleClearImportHistory}
                      okText="Sim, limpar"
                      cancelText="Cancelar"
                      okButtonProps={{ danger: true }}
                    >
                      <Button 
                        danger 
                        icon={<DeleteOutlined />}
                        loading={clearingHistory}
                        disabled={auditLogs.length === 0}
                      >
                        Limpar Histórico
                      </Button>
                    </Popconfirm>
                  </div>

                  <Table
                    dataSource={auditLogs}
                    columns={[
                      {
                        title: 'Data/Hora',
                        dataIndex: 'imported_at',
                        key: 'imported_at',
                        render: (date: string) => (
                          <div>
                            {new Date(date).toLocaleDateString('pt-PT')}
                            <br />
                            <span style={{ fontSize: '12px', color: '#666' }}>
                              {new Date(date).toLocaleTimeString('pt-PT')}
                            </span>
                          </div>
                        )
                      },
                      {
                        title: 'Utilizador',
                        dataIndex: 'imported_by_user',
                        key: 'imported_by_user'
                      },
                      {
                        title: 'Tipo',
                        dataIndex: 'import_type',
                        key: 'import_type',
                        render: (type: string) => (
                          <Tag color={type === '4000Junho' ? 'blue' : 'green'}>
                            {type}
                          </Tag>
                        )
                      },
                      {
                        title: 'Ficheiro',
                        dataIndex: 'file_name',
                        key: 'file_name',
                        render: (name: string) => (
                          <div style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {name}
                          </div>
                        )
                      },
                      {
                        title: 'Estatísticas',
                        key: 'stats',
                        render: (_, record: any) => (
                          <div>
                            <div><strong>Total:</strong> {record.total_rows}</div>
                            <div><strong>Importados:</strong> {record.imported_count}</div>
                            <div><strong>Duplicados:</strong> {record.duplicate_count}</div>
                            <div><strong>Erros:</strong> {record.error_count}</div>
                          </div>
                        )
                      },
                      {
                        title: 'Status',
                        dataIndex: 'status',
                        key: 'status',
                        render: (status: string) => (
                          <Tag color={status === 'completed' ? 'green' : status === 'partial' ? 'orange' : 'red'}>
                            {status === 'completed' ? 'Concluído' : status === 'partial' ? 'Parcial' : 'Falhou'}
                          </Tag>
                        )
                      }
                    ]}
                    pagination={{
                      pageSize: 10,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} importações`
                    }}
                  />
                </div>
              )
            }
          ]}
        />
      </Modal>

      {/* Lead Details Modal */}
      <LeadDetailsModal
        open={leadDetailsModalVisible}
        lead={selectedLeadForDetails}
        onClose={() => setLeadDetailsModalVisible(false)}
      />

    </div>
  );
};

export default Leads; 